// Table view: every person as a sortable row, plus what's MISSING.
//
// The other views render absent data as absent pixels - a person with no
// birthday looks identical to one with a birthday, and someone with no
// relationship edges is a card parked off to one side you may never scroll to.
// A tree you are actively building needs the inverse of a chart: a list you can
// sort by the column that is empty. Hence the gap chips at the top, which
// filter rather than merely count.
import { birthOf, ageOf, deathOf, personOpenable, applyPersonLink } from "./genealogy"
// Shared with the chart, which counts the same gaps so a member sees the
// worklist without having to find this view first.
import { GAPS } from "./gaps"

const COLUMNS = [
    { key: "name", label: "Name", sort: (a, b) => a.name.localeCompare(b.name) },
    { key: "relation", label: "Relation", sort: (a, b) => str(a.relation).localeCompare(str(b.relation)) },
    // Two columns, not one lifespan. The table is the only view with room for
    // whole dates and the only one you come to in order to CHECK a date - a
    // member entered "4 december 1923" and wanted to see it back. A range in one
    // cell answered a different question, and read as a mangled birth date.
    { key: "born", label: "Born", sort: (a, b) => dateKey(a) - dateKey(b) },
    { key: "died", label: "Died", sort: (a, b) => deathKey(a) - deathKey(b) },
    { key: "age", label: "Age", sort: (a, b) => num(ageOf(a)) - num(ageOf(b)) },
    { key: "location", label: "Place", sort: (a, b) => str(a.location).localeCompare(str(b.location)) }
]

// A large one-name study can have thousands of people. Painting every cell
// before first render made the table sit on "Loading…" for several seconds and
// left ten thousand-plus DOM nodes behind. Keep the initial document small;
// readers can progressively reveal it, including broad search results.
const PAGE_SIZE = 100

// Blanks sort last in either direction, so ascending-by-birthday opens on the
// oldest person rather than on a wall of people with no date.
const BLANK = Number.MAX_SAFE_INTEGER
const str = (v) => (v == null || v === "" ? "￿" : String(v))
const num = (v) => (v == null ? BLANK : v)
const dateKey = (node) => {
    let birth = birthOf(node)
    return birth.known ? birth.date.getTime() : BLANK
}



export function renderTable(ctx) {
    let wrap = document.createElement("div")
    wrap.className = "ft-table"
    // Sort and filter are view-local: they are how you read this table, not
    // something worth persisting or putting in the URL.
    // The gap can arrive from outside: the chart's "still to finish" line links
    // straight into the filtered table, so the thing you clicked is the thing
    // you land on.
    let state = { sort: "name", dir: 1, gap: ctx.gap || null, limit: PAGE_SIZE, searchIds: null }

    // Place is owner-only data (Contacts::TreePayload#public_node carries no
    // location), so on a shared tree the column would be a strip of em dashes
    // and a "No place: 12" chip counting a gap the visitor cannot see, let
    // alone fill. Drop both wherever nothing in the payload has a location.
    let hasPlace = ctx.nodes.some((node) => node.location)
    let columns = COLUMNS.filter((column) => hasPlace || column.key !== "location")
    let gaps = GAPS.filter((gap) => hasPlace || gap.key !== "no_place")

    let gapBar = document.createElement("div")
    gapBar.className = "ft-table-gaps"
    let table = document.createElement("table")
    table.className = "ft-table-grid"
    let head = document.createElement("thead")
    let body = document.createElement("tbody")
    let more = document.createElement("div")
    more.className = "ft-table-more"
    table.append(head, body)

    let counts = new Map(gaps.map((gap) => [gap.key, ctx.nodes.filter((n) => gap.test(n, ctx)).length]))

    function draw() {
        let focused = document.activeElement
        let focusSort = head.contains(focused) ? focused.dataset.sort : null
        let focusGap = gapBar.contains(focused) ? focused.dataset.gap : null
        let focusMore = more.contains(focused)
        let previousCount = body.rows.length
        gapBar.innerHTML = ""
        // The app's standard filter chip (.msg-chip, shared/_filter_chips_dropdown
        // and the task toolbar), with the count as a badge rather than baked
        // into the label as "No birthday: 7". These sit on the same row as the
        // layout switcher, which is a chip too, so anything else reads as a
        // stray control. The people count that used to head this row now lives
        // in the search box.
        gaps.forEach((gap) => {
            let count = counts.get(gap.key)
            if (!count) return
            let active = state.gap === gap.key
            let chip = document.createElement("button")
            chip.type = "button"
            chip.dataset.gap = gap.key
            chip.className = `msg-chip msg-chip--compact${active ? " is-active" : ""}`
            chip.setAttribute("aria-pressed", String(active))
            let label = document.createElement("span")
            label.textContent = gap.label
            let badge = document.createElement("span")
            badge.className = "msg-chip-count msg-chip-count--muted"
            badge.textContent = String(count)
            chip.append(label, badge)
            chip.addEventListener("click", () => {
                state.gap = active ? null : gap.key
                state.limit = PAGE_SIZE
                draw()
            })
            gapBar.appendChild(chip)
        })

        head.innerHTML = ""
        let hrow = document.createElement("tr")
        columns.forEach((column) => {
            let th = document.createElement("th")
            th.scope = "col"
            th.className = `ft-table-col--${column.key}`
            let button = document.createElement("button")
            button.type = "button"
            button.className = "ft-table-sort"
            button.dataset.sort = column.key
            button.textContent = column.label
            if (state.sort === column.key) {
                button.classList.add("is-active")
                button.appendChild(Object.assign(document.createElement("i"), { className: `bi ${state.dir > 0 ? "bi-caret-up-fill" : "bi-caret-down-fill"}` }))
                th.setAttribute("aria-sort", state.dir > 0 ? "ascending" : "descending")
            }
            button.addEventListener("click", () => {
                if (state.sort === column.key) state.dir *= -1
                else { state.sort = column.key; state.dir = 1 }
                draw()
            })
            th.appendChild(button)
            hrow.appendChild(th)
        })
        head.appendChild(hrow)

        let gap = gaps.find((g) => g.key === state.gap)
        let rows = ctx.nodes.filter((node) => !gap || gap.test(node, ctx))
        if (state.searchIds) rows = rows.filter((node) => state.searchIds.has(String(node.id)))
        let column = columns.find((c) => c.key === state.sort) || columns[0]
        // Missing values remain last when reversing the known values.
        let value = (node) => {
            switch (column.key) {
            case "born": return dateKey(node)
            case "died": return deathKey(node)
            case "age": return num(ageOf(node))
            default: return str(node[column.key])
            }
        }
        rows = rows.slice().sort((a, b) => {
            let missingA = [BLANK, "￿"].includes(value(a))
            let missingB = [BLANK, "￿"].includes(value(b))
            return missingA !== missingB ? Number(missingA) - Number(missingB) : column.sort(a, b) * state.dir
        })
        let visibleRows = rows.slice(0, state.limit)

        body.innerHTML = ""
        visibleRows.forEach((node) => body.appendChild(row(node, ctx, hasPlace)))
        if (!rows.length) {
            let tr = document.createElement("tr")
            let td = document.createElement("td")
            td.colSpan = columns.length
            td.className = "ft-table-none"
            td.textContent = "Nobody matches that filter."
            tr.appendChild(td)
            body.appendChild(tr)
        }

        more.replaceChildren()
        if (visibleRows.length < rows.length) {
            let summary = document.createElement("span")
            summary.textContent = `Showing ${visibleRows.length.toLocaleString()} of ${rows.length.toLocaleString()}`
            let reveal = document.createElement("button")
            reveal.type = "button"
            reveal.className = "btn btn-sm btn-outline-secondary"
            reveal.textContent = `Show ${Math.min(PAGE_SIZE, rows.length - visibleRows.length).toLocaleString()} more`
            reveal.addEventListener("click", () => {
                state.limit += PAGE_SIZE
                draw()
            })
            more.append(summary, reveal)
        }
        if (focusSort) head.querySelector(`[data-sort="${focusSort}"]`)?.focus({ preventScroll: true })
        if (focusGap) gapBar.querySelector(`[data-gap="${focusGap}"]`)?.focus({ preventScroll: true })
        if (focusMore) body.rows[previousCount]?.querySelector("a")?.focus({ preventScroll: true })
    }

    draw()
    // Gap chips are filters, so they belong on the toolbar row with the search
    // and the layout switcher (see contacts/_tree). Only fall back to stacking
    // them above the table where there is no slot - the org chart mounts the
    // same controller without the family tree's toolbar.
    if (ctx.filterSlot) {
        ctx.filterSlot.appendChild(gapBar)
        wrap.append(table, more)
    } else {
        wrap.append(gapBar, table, more)
    }
    return {
        element: wrap,
        panZoom: false,
        search(matchIds, active) {
            state.searchIds = active ? new Set(matchIds.map(String)) : null
            state.limit = PAGE_SIZE
            draw()
        }
    }
}

// Whole dates where they are known, the part that is known where they are not.
// A year-only date says the year; a day-and-month-only date says the day and
// month; neither pretends to be more precise than it is.
function fullDate(iso) {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

function bornText(node) {
    if (node.birthday && node.birth_year_known) return fullDate(node.birthday)
    // Day and month known, year not (Contact::BIRTHDAY_NO_YEAR).
    if (node.birthday) return new Date(`${node.birthday}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    if (Number.isFinite(node.birth_year)) return String(node.birth_year)
    return "—"
}

function diedText(node) {
    if (!node.deceased) return ""
    if (node.died_on && node.death_year_known) return fullDate(node.died_on)
    if (Number.isFinite(node.death_year)) return String(node.death_year)
    return "—"
}

function deathKey(node) {
    if (node.died_on && node.death_year_known) return new Date(`${node.died_on}T00:00:00`).getTime()
    if (Number.isFinite(node.death_year)) return new Date(node.death_year, 6, 1).getTime()
    return BLANK
}

function row(node, ctx, hasPlace) {
    let tr = document.createElement("tr")
    tr.className = "ft-table-row"
    tr.dataset.nodeId = node.id

    let nameCell = document.createElement("td")
    nameCell.className = "ft-table-name"
    let name = document.createElement(personOpenable(node) ? "a" : "span")
    if (personOpenable(node)) applyPersonLink(name, node)
    name.textContent = node.name
    nameCell.appendChild(name)
    if (!ctx.linked.has(node.id)) {
        nameCell.appendChild(Object.assign(document.createElement("span"), { className: "ft-table-flag", textContent: "not linked" }))
    }
    tr.appendChild(nameCell)

    tr.appendChild(cell(node.relation, "ft-table-relation"))

    tr.appendChild(cell(bornText(node), "ft-table-birthday"))
    // "Died, date unknown" is the most useful row in this table, and an em dash
    // beside a name you know is gone is the question. Empty for the living, so
    // the column reads as a fact rather than a blank to be filled.
    // cell() prints an em dash for empty, which is right for a MISSING date and
    // wrong for a living person - "—" under Died reads as "nobody knows", and
    // the whole point of this column is that an em dash is a question somebody
    // in the family can answer.
    let died = diedText(node)
    let diedCell = node.deceased ? cell(died, "ft-table-birthday") : cell(" ", "ft-table-birthday")
    if (node.deceased && died === "—") diedCell.title = "Nobody has this date yet"
    tr.appendChild(diedCell)

    // Age AT DEATH for somebody gone, and nothing at all when their date is
    // unknown - "104" is a statement about a person who has not been alive for
    // years, which is what this column used to say.
    let age = ageOf(node)
    let ageCell = cell(age == null ? "—" : String(age), "ft-table-age")
    if (age != null && node.deceased) ageCell.title = `Aged ${age} when they died`
    tr.appendChild(ageCell)
    if (hasPlace) tr.appendChild(cell(node.location, "ft-table-place"))
    return tr
}

function cell(value, className) {
    let td = document.createElement("td")
    td.className = className
    td.textContent = value == null || value === "" ? "—" : value
    return td
}
